import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { Gift, Loader2, Save, Search, Users as UsersIcon, Wallet, Clock, CheckCircle2, XCircle, Power, Package, Zap, ShieldCheck } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DEFAULTS = {
  enabled: false,
  referrerAmount: 50,
  refereeAmount: 50,
  trigger: 'FIRST_ORDER',
  minOrderAmount: 0,
  maxReferralsPerUser: 0,
  applyWindowHours: 72
};

const STATUS_STYLES = {
  PENDING: 'bg-amber-100 text-amber-800',
  COMPLETED: 'bg-green-100 text-green-800',
  REJECTED: 'bg-gray-200 text-gray-700'
};

const rupee = (n) => `₹${Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
const fmt = (d) => (d ? format(new Date(d), 'dd MMM yyyy, hh:mm a') : '—');

const ReferEarn = () => {
  const [form, setForm] = useState(DEFAULTS);
  const [savedForm, setSavedForm] = useState(DEFAULTS);
  const [meta, setMeta] = useState({ updatedAt: null, updatedByName: null });
  const [loadingSettings, setLoadingSettings] = useState(true);
  const [saving, setSaving] = useState(false);

  const [stats, setStats] = useState(null);
  const [statsError, setStatsError] = useState(false);
  const [rows, setRows] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, totalPages: 1, total: 0 });
  const [status, setStatus] = useState('all');
  const [search, setSearch] = useState('');
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(1);
  const [loadingList, setLoadingList] = useState(true);
  const [rejectingId, setRejectingId] = useState(null);

  const loadSettings = useCallback(async () => {
    setLoadingSettings(true);
    try {
      const res = await axios.get(`${API}/referrals/settings`);
      const loaded = { ...DEFAULTS, ...(res.data?.settings || {}) };
      setForm(loaded);
      setSavedForm(loaded);
      setMeta({ updatedAt: res.data?.updatedAt, updatedByName: res.data?.updatedByName });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load referral settings');
    } finally {
      setLoadingSettings(false);
    }
  }, []);

  const loadStats = useCallback(async () => {
    try {
      setStatsError(false);
      const res = await axios.get(`${API}/referrals/stats`);
      setStats(res.data);
    } catch (e) {
      console.error('Referral stats failed', e);
      setStatsError(true);
    }
  }, []);

  const loadList = useCallback(async () => {
    setLoadingList(true);
    try {
      const res = await axios.get(`${API}/referrals`, {
        params: { page, limit: 20, ...(status !== 'all' ? { status } : {}), ...(query ? { search: query } : {}) }
      });
      setRows(res.data?.data || []);
      setPagination(res.data?.pagination || { page: 1, totalPages: 1, total: 0 });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load referrals');
    } finally {
      setLoadingList(false);
    }
  }, [page, status, query]);

  useEffect(() => { loadSettings(); loadStats(); }, [loadSettings, loadStats]);
  useEffect(() => { loadList(); }, [loadList]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async (e) => {
    e?.preventDefault();
    setSaving(true);
    try {
      const res = await axios.put(`${API}/referrals/settings`, {
        ...form,
        referrerAmount: Number(form.referrerAmount),
        refereeAmount: Number(form.refereeAmount),
        minOrderAmount: Number(form.minOrderAmount),
        maxReferralsPerUser: Number(form.maxReferralsPerUser),
        applyWindowHours: Number(form.applyWindowHours)
      });
      const saved = { ...DEFAULTS, ...(res.data?.settings || {}) };
      setForm(saved);
      setSavedForm(saved);
      setMeta({ updatedAt: res.data?.updatedAt, updatedByName: res.data?.updatedByName });
      toast.success(res.data?.message || 'Saved');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const reject = async (row) => {
    const reason = window.prompt('Reason for rejecting this referral (no reward will be paid):', 'Suspected misuse');
    if (reason === null) return;
    setRejectingId(row.id);
    try {
      await axios.post(`${API}/referrals/${row.id}/reject`, { reason });
      toast.success('Referral rejected');
      loadList();
      loadStats();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reject');
    } finally {
      setRejectingId(null);
    }
  };

  const ruleText = form.trigger === 'SIGNUP'
    ? 'Paid as soon as your friend signs up with your code.'
    : `Paid when your friend's first order${Number(form.minOrderAmount) > 0 ? ` of ${rupee(form.minOrderAmount)}+` : ''} is delivered.`;

  const dirty = JSON.stringify(normalizeForCompare(form)) !== JSON.stringify(normalizeForCompare(savedForm));
  const statValue = (v, money = false) => (stats ? (money ? rupee(v) : Number(v || 0).toLocaleString('en-IN')) : null);

  return (
    <div className="space-y-6 p-4 sm:p-6 lg:p-8 max-w-[1600px] mx-auto" data-testid="refer-earn-page">
      {/* ───────── Header ───────── */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3">
          <div className="h-11 w-11 rounded-xl bg-orange-100 flex items-center justify-center">
            <Gift className="text-orange-600" size={22} />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-2xl font-semibold tracking-tight">Refer &amp; Earn</h1>
              {!loadingSettings && (
                <Badge className={`${savedForm.enabled ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'} border-0`}>
                  {savedForm.enabled ? 'Live' : 'Paused'}
                </Badge>
              )}
            </div>
            <p className="text-sm text-gray-500">Wallet cashback for the user who refers and the new user who joins.</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {meta.updatedAt && (
            <span className="hidden md:inline text-xs text-gray-500">
              Updated {fmt(meta.updatedAt)}{meta.updatedByName ? ` · ${meta.updatedByName}` : ''}
            </span>
          )}
          <Button onClick={save} disabled={saving || loadingSettings || !dirty}
                  style={{ backgroundColor: '#C2410C' }} data-testid="referral-save">
            {saving ? <Loader2 className="mr-2 animate-spin" size={16} /> : <Save className="mr-2" size={16} />}
            {dirty ? 'Save changes' : 'Saved'}
          </Button>
        </div>
      </div>

      {/* ───────── Stats ───────── */}
      <div className="grid grid-cols-2 xl:grid-cols-4 gap-4">
        {[
          { label: 'Total referrals', value: statValue(stats?.total), icon: UsersIcon, tone: 'bg-blue-50 text-blue-600' },
          { label: 'Pending', value: statValue(stats?.pending), icon: Clock, tone: 'bg-amber-50 text-amber-600' },
          { label: 'Completed', value: statValue(stats?.completed), icon: CheckCircle2, tone: 'bg-green-50 text-green-600' },
          {
            label: 'Paid to wallets',
            value: statValue(stats?.totalPaid, true),
            icon: Wallet,
            tone: 'bg-orange-50 text-orange-600',
            sub: stats ? `Referrers ${rupee(stats.referrerPaid)} · New users ${rupee(stats.refereePaid)}` : null
          }
        ].map((s) => (
          <div key={s.label} className="rounded-xl border bg-white p-4 flex items-start gap-3 shadow-sm">
            <div className={`h-10 w-10 shrink-0 rounded-lg flex items-center justify-center ${s.tone}`}>
              <s.icon size={20} />
            </div>
            <div className="min-w-0">
              <div className="text-xs font-medium text-gray-500">{s.label}</div>
              {s.value === null ? (
                statsError
                  ? <div className="text-sm text-red-600 mt-1">Couldn't load</div>
                  : <div className="h-6 w-16 mt-1 rounded bg-gray-100 animate-pulse" />
              ) : (
                <div className="text-2xl font-semibold leading-tight mt-0.5">{s.value}</div>
              )}
              {s.sub && <div className="text-[11px] text-gray-500 mt-1 truncate">{s.sub}</div>}
            </div>
          </div>
        ))}
      </div>

      {/* ───────── Rules + Preview ───────── */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        <Card className="xl:col-span-2 shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg">Referral rules</CardTitle>
            <p className="text-sm text-gray-500">Applies to new referrals. Existing ones keep the amounts they were promised.</p>
          </CardHeader>
          <CardContent>
            {loadingSettings ? (
              <div className="space-y-3">
                {[1, 2, 3].map((i) => <div key={i} className="h-12 rounded-lg bg-gray-100 animate-pulse" />)}
              </div>
            ) : (
              <form onSubmit={save} className="space-y-7">
                {/* Program switch */}
                <div className={`flex items-center justify-between gap-4 rounded-xl border p-4 transition-colors ${form.enabled ? 'border-green-200 bg-green-50/60' : 'bg-gray-50'}`}>
                  <div>
                    <div className="font-medium flex items-center gap-2">
                      <Power size={16} className={form.enabled ? 'text-green-600' : 'text-gray-400'} />
                      Program {form.enabled ? 'active' : 'paused'}
                    </div>
                    <div className="text-sm text-gray-500 mt-0.5">
                      {form.enabled
                        ? 'Users can enter referral codes at signup.'
                        : 'New codes are not accepted. Referrals already applied still complete.'}
                    </div>
                  </div>
                  <Switch checked={!!form.enabled} onCheckedChange={(v) => set('enabled', v)}
                          className="scale-125 data-[state=checked]:bg-green-600" data-testid="referral-enabled" />
                </div>

                {/* Rewards */}
                <section className="space-y-3">
                  <SectionTitle n="1" title="Rewards" hint="Credited to the FUDK wallet" />
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <MoneyField label="Referrer gets" help="User who shares the code"
                                value={form.referrerAmount} onChange={(v) => set('referrerAmount', v)} testId="referrer-amount" />
                    <MoneyField label="New user gets" help="User who signs up with the code"
                                value={form.refereeAmount} onChange={(v) => set('refereeAmount', v)} testId="referee-amount" />
                  </div>
                </section>

                {/* Trigger */}
                <section className="space-y-3">
                  <SectionTitle n="2" title="When to pay" hint="Both users are paid at the same moment" />
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {[
                      { v: 'FIRST_ORDER', title: 'After first order is delivered', desc: 'Recommended — rewards real customers only', icon: Package },
                      { v: 'SIGNUP', title: 'Instantly on signup', desc: 'Faster growth, higher misuse risk', icon: Zap }
                    ].map((o) => {
                      const active = form.trigger === o.v;
                      return (
                        <button type="button" key={o.v} onClick={() => set('trigger', o.v)}
                                data-testid={`referral-trigger-${o.v}`}
                                className={`text-left rounded-xl border-2 p-4 transition-all ${active ? 'border-orange-500 bg-orange-50/60 shadow-sm' : 'border-gray-200 hover:border-gray-300'}`}>
                          <div className="flex items-start gap-3">
                            <o.icon size={18} className={active ? 'text-orange-600 mt-0.5' : 'text-gray-400 mt-0.5'} />
                            <div className="flex-1">
                              <div className="font-medium text-sm">{o.title}</div>
                              <div className="text-xs text-gray-500 mt-0.5">{o.desc}</div>
                            </div>
                            <span className={`h-4 w-4 rounded-full border-2 mt-0.5 ${active ? 'border-orange-500 bg-orange-500 ring-2 ring-orange-200' : 'border-gray-300'}`} />
                          </div>
                        </button>
                      );
                    })}
                  </div>
                  {form.trigger === 'FIRST_ORDER' && (
                    <div className="md:w-1/2">
                      <MoneyField label="Minimum first-order value" help="Item total, before fees. 0 = any order"
                                  value={form.minOrderAmount} onChange={(v) => set('minOrderAmount', v)} />
                    </div>
                  )}
                </section>

                {/* Limits */}
                <section className="space-y-3">
                  <SectionTitle n="3" title="Limits" hint="Protects against misuse" />
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <NumberField label="Max referrals per user" help="0 = unlimited" suffix="referrals"
                                 value={form.maxReferralsPerUser} onChange={(v) => set('maxReferralsPerUser', v)} />
                    <NumberField label="Code entry window" help="New users can add a code this long after signing up" suffix="hours"
                                 value={form.applyWindowHours} min={1} onChange={(v) => set('applyWindowHours', v)} />
                  </div>
                </section>

                <div className="flex justify-end border-t pt-4">
                  <Button type="submit" disabled={saving || !dirty} style={{ backgroundColor: '#C2410C' }}>
                    {saving ? <Loader2 className="mr-2 animate-spin" size={16} /> : <Save className="mr-2" size={16} />}
                    {dirty ? 'Save changes' : 'All changes saved'}
                  </Button>
                </div>
              </form>
            )}
          </CardContent>
        </Card>

        {/* Live preview */}
        <div className="space-y-4">
          <Card className="shadow-sm overflow-hidden">
            <div className="bg-gradient-to-br from-green-600 to-green-500 p-5 text-white">
              <div className="text-xs uppercase tracking-wide text-green-100">Customer preview</div>
              <div className="text-xl font-semibold mt-1">
                You get {rupee(form.referrerAmount)} · Friend gets {rupee(form.refereeAmount)}
              </div>
              <div className="text-sm text-green-50 mt-1">{ruleText}</div>
            </div>
            <CardContent className="p-5 space-y-3">
              {[
                'Share your referral code with friends',
                'Friend enters the code while signing up',
                form.trigger === 'SIGNUP'
                  ? 'Both wallets are credited instantly'
                  : `Friend's first order${Number(form.minOrderAmount) > 0 ? ` of ${rupee(form.minOrderAmount)}+` : ''} is delivered → both wallets credited`
              ].map((t, i) => (
                <div key={i} className="flex items-start gap-3 text-sm">
                  <span className="h-6 w-6 shrink-0 rounded-full bg-green-100 text-green-700 text-xs font-semibold flex items-center justify-center">{i + 1}</span>
                  <span className="pt-0.5">{t}</span>
                </div>
              ))}
            </CardContent>
          </Card>

          <Card className="shadow-sm">
            <CardContent className="p-5">
              <div className="flex items-center gap-2 font-medium text-sm mb-3">
                <ShieldCheck size={16} className="text-green-600" /> Built-in safeguards
              </div>
              <ul className="space-y-2 text-sm text-gray-600">
                {[
                  'A user can be referred only once',
                  'Only new users before their first order',
                  "Users can't use their own code",
                  'Same device can’t be on both sides',
                  'Each reward is paid exactly once'
                ].map((t) => (
                  <li key={t} className="flex items-start gap-2">
                    <CheckCircle2 size={14} className="text-green-600 mt-0.5 shrink-0" /> {t}
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* ───────── Referrals list ───────── */}
      <Card className="shadow-sm">
        <CardHeader className="pb-3">
          <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <CardTitle className="text-lg">
              Referrals <span className="text-gray-400 font-normal text-base">({pagination.total || 0})</span>
            </CardTitle>
            <div className="flex flex-wrap gap-2">
              <form className="relative" onSubmit={(e) => { e.preventDefault(); setPage(1); setQuery(search.trim()); }}>
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <Input className="pl-9 w-64" placeholder="Name, phone or code — press Enter"
                       value={search} onChange={(e) => setSearch(e.target.value)} />
              </form>
              <div className="inline-flex rounded-lg border bg-gray-50 p-1">
                {[['all', 'All'], ['PENDING', 'Pending'], ['COMPLETED', 'Completed'], ['REJECTED', 'Rejected']].map(([v, l]) => (
                  <button key={v} type="button" onClick={() => { setStatus(v); setPage(1); }}
                          className={`px-3 py-1 text-sm rounded-md transition-colors ${status === v ? 'bg-white shadow-sm font-medium text-gray-900' : 'text-gray-500 hover:text-gray-800'}`}>
                    {l}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4 px-6 pb-6">
          <div className="overflow-x-auto rounded-lg border">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-500">
                <tr>
                  <th className="px-4 py-3 font-medium">Referrer</th>
                  <th className="px-4 py-3 font-medium">New user</th>
                  <th className="px-4 py-3 font-medium">Code</th>
                  <th className="px-4 py-3 font-medium">Rewards</th>
                  <th className="px-4 py-3 font-medium">Status</th>
                  <th className="px-4 py-3 font-medium">Date</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {loadingList ? (
                  [1, 2, 3].map((i) => (
                    <tr key={i}><td colSpan={7} className="px-4 py-3"><div className="h-8 rounded bg-gray-100 animate-pulse" /></td></tr>
                  ))
                ) : rows.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-4 py-14 text-center">
                      <Gift className="mx-auto text-gray-300" size={36} />
                      <div className="mt-2 font-medium text-gray-700">No referrals {status !== 'all' || query ? 'match this filter' : 'yet'}</div>
                      <div className="text-sm text-gray-500">
                        {savedForm.enabled ? 'They’ll appear here as soon as a new user signs up with a code.' : 'Turn the program on to start accepting referral codes.'}
                      </div>
                    </td>
                  </tr>
                ) : rows.map((r) => (
                  <tr key={r.id} className="hover:bg-gray-50/70">
                    <td className="px-4 py-3"><Person p={r.referrer} /></td>
                    <td className="px-4 py-3"><Person p={r.referee} /></td>
                    <td className="px-4 py-3"><span className="font-mono text-xs rounded bg-gray-100 px-2 py-1">{r.code}</span></td>
                    <td className="px-4 py-3 whitespace-nowrap text-xs">
                      <div className="flex items-center gap-1">{rupee(r.referrerAmount)} <span className="text-gray-400">referrer</span>{r.referrerCredited && <CheckCircle2 size={12} className="text-green-600" />}</div>
                      <div className="flex items-center gap-1 mt-0.5">{rupee(r.refereeAmount)} <span className="text-gray-400">new user</span>{r.refereeCredited && <CheckCircle2 size={12} className="text-green-600" />}</div>
                      <div className="text-gray-400 mt-0.5">{r.trigger === 'SIGNUP' ? 'On signup' : `First order${r.minOrderAmount > 0 ? ` ≥ ${rupee(r.minOrderAmount)}` : ''}`}</div>
                    </td>
                    <td className="px-4 py-3">
                      <Badge className={`${STATUS_STYLES[r.status] || ''} border-0`}>{r.status.charAt(0) + r.status.slice(1).toLowerCase()}</Badge>
                      {r.status === 'REJECTED' && r.rejectedReason && <div className="text-xs text-gray-500 mt-1 max-w-[180px]">{r.rejectedReason}</div>}
                    </td>
                    <td className="px-4 py-3 text-xs whitespace-nowrap text-gray-600">
                      <div>{fmt(r.createdAt)}</div>
                      {r.completedAt && <div className="text-green-700">Paid {fmt(r.completedAt)}</div>}
                    </td>
                    <td className="px-4 py-3 text-right">
                      {r.status === 'PENDING' && (
                        <Button size="sm" variant="ghost" className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                disabled={rejectingId === r.id} onClick={() => reject(r)}>
                          <XCircle size={14} className="mr-1" /> Reject
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {(pagination.totalPages || 1) > 1 && (
            <div className="flex items-center justify-end gap-2 text-sm text-gray-600">
              <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Previous</Button>
              <span className="px-2">Page {pagination.page || page} of {pagination.totalPages}</span>
              <Button size="sm" variant="outline" disabled={page >= pagination.totalPages} onClick={() => setPage((p) => p + 1)}>Next</Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

/* ───────── small building blocks ───────── */
const normalizeForCompare = (f) => ({
  enabled: !!f.enabled,
  trigger: f.trigger,
  referrerAmount: Number(f.referrerAmount),
  refereeAmount: Number(f.refereeAmount),
  minOrderAmount: Number(f.minOrderAmount),
  maxReferralsPerUser: Number(f.maxReferralsPerUser),
  applyWindowHours: Number(f.applyWindowHours)
});

const SectionTitle = ({ n, title, hint }) => (
  <div className="flex items-baseline gap-2">
    <span className="h-5 w-5 rounded-full bg-orange-100 text-orange-700 text-[11px] font-semibold inline-flex items-center justify-center">{n}</span>
    <span className="font-medium">{title}</span>
    {hint && <span className="text-xs text-gray-500">· {hint}</span>}
  </div>
);

const MoneyField = ({ label, help, value, onChange, testId }) => (
  <div>
    <Label className="text-sm">{label}</Label>
    <div className="relative mt-1.5">
      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm">₹</span>
      <Input type="number" min="0" step="1" className="pl-7" value={value}
             onChange={(e) => onChange(e.target.value)} data-testid={testId} />
    </div>
    {help && <p className="text-xs text-gray-500 mt-1">{help}</p>}
  </div>
);

const NumberField = ({ label, help, value, onChange, suffix, min = 0 }) => (
  <div>
    <Label className="text-sm">{label}</Label>
    <div className="relative mt-1.5">
      <Input type="number" min={min} step="1" className="pr-20" value={value} onChange={(e) => onChange(e.target.value)} />
      {suffix && <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-xs">{suffix}</span>}
    </div>
    {help && <p className="text-xs text-gray-500 mt-1">{help}</p>}
  </div>
);

const Person = ({ p }) => {
  const label = p?.name || p?.phone || 'Unknown';
  return (
    <div className="flex items-center gap-2 min-w-[150px]">
      <div className="h-8 w-8 rounded-full bg-orange-100 text-orange-700 flex items-center justify-center text-xs font-semibold">
        {String(label).trim().charAt(0).toUpperCase()}
      </div>
      <div className="leading-tight">
        <div className="font-medium text-gray-900">{p?.name || '—'}</div>
        {p?.phone && <div className="text-xs text-gray-500">{p.phone}</div>}
      </div>
    </div>
  );
};

export default ReferEarn;
